import ExportArtifactOption from "../ExportArtifactOption";
import React, { useMemo } from "react";
import * as io from "io-ts";
import cx from "classnames";
import { useDelete, usePost } from "../../../../../api/useCall";
import createIOSortType from "../../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import useToasts from "@ui/Toast/useToasts";
import type { SortableValue } from "diagnostics-lib/comparator";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import ModalForm from "@ui/Modal/Form";
import useSortedData from "../../../../../lib/hooks/useSortedData";
import t from "../../../../../i18n";
import Table from "@ui/Table";
import { useQuantities } from "../../../../../api/useQuantities";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import type { Project } from "../../../../../api/useProjects";
import { useSetups, type Setup } from "../../../../../api/useSetups";
import { useCanUserDoOnProject } from "../../../../canUserDo";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import BlankStateV2 from "@ui/BlankStateV2";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import Text from "@ui/Text";
import DropzoneV2 from "@ui/DropzoneV2";
import type {
  InputModelParsingError,
  InputModelParsingResult,
} from "../../../../../domain/parser/inputModel/csv";
import { parse as parseInputModelFile } from "../../../../../domain/parser/inputModel/csv";
import { isResultErr } from "../../../../../lib/patterns/result";
import { getInputModelParsingErrorMessage } from "../../../../../domain/parser/inputModel/csv/errors";
import TableCard from "@ui/Table/Card";
import Menu from "@ui/Menu";
import Option, { OptionGroup } from "@ui/Option";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, Link, More2Fill, UploadLine } from "@ui/IconV2/react";
import { color } from "@ui";
import TooltipV2 from "@ui/TooltipV2";
import {
  TableCardWithoutBorder,
  TableHeaderNoPaddingT,
} from "@ui/Table/_styles/TableCardWithoutBorder";
import Card from "@ui/Card";
import { useImpactedAnalyses } from "../useAnalysisImpacted";
import useActiveAnalysesStream from "../../../../../api/useActiveAnalysesStream";
import type { QuantityIndex } from "../../../../../api/useQuantities";
import { getCorpusGraph } from "diagnostics-lib/runtime";

const columns = ["name", "modelName", "unit", "orderExternalGap", "referenceValue"] as const;
type Column = (typeof columns)[number];

const useSortInput = createGlobalState({
  storage: "session",
  key: "setupInputBindingSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: true,
});

interface ModelBinding {
  quantityName: string;
  modelName: string;
  unit: string | null;
  orderExternalGap: number | null;
  referenceValue: number | null;
}

const useInputBindingSearch = createGlobalState({
  storage: "session",
  key: "inputBindingSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});

const computeRowKey = (binding: ModelBinding) => binding.quantityName;

const sortSelector: { [key in Column]: (b: ModelBinding) => SortableValue | null } = {
  name: b => b.quantityName,
  modelName: b => b.modelName,
  unit: b => b.unit,
  orderExternalGap: b => b.orderExternalGap,
  referenceValue: b => b.referenceValue,
};

interface DeleteInputBindingModalProps {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
}

const analysisStatus = new Set([
  "SIMULATION",
  "SIMULATION_ERROR",
  "DIAGNOSIS",
  "DIAGNOSIS_ERROR",
] as const);

export const DeleteInputBindingModal = ({
  onRequestClose,
  project,
  setup,
}: DeleteInputBindingModalProps) => {
  const setupId = setup.id;
  const projectId = project.id;
  const deleteSimulationInputs = useDelete(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/inputs",
  );
  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalyses = useImpactedAnalyses({
    setups: useSetups(projectId),
    activeAnalyses,
    setupId,
    analysisStatus,
  });
  const showAdditionalMessage = setup.simulationStatus === "VALID" && impactedAnalyses.length > 0;

  const handleSubmit = React.useCallback(async () => {
    await deleteSimulationInputs({
      routeParams: { projectId, setupId },
    });
    return true;
  }, [deleteSimulationInputs, projectId, setupId]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={handleSubmit}
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
    >
      <Text>{t("setup.paragraphs.model.modal.delete_input.text")}</Text>
      {showAdditionalMessage ? (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.modal.delete.analysis.additionnal_message.warning", {
            simulationValid: setup.simulationStatus === "VALID",
            diagnosisValid: setup.diagnosisStatus === "COMPLETE",
            impactedAnalysisCount: impactedAnalyses.length,
          })}
        />
      ) : null}
    </ModalForm>
  );
};

const computeInputs = (setup: Setup, quantityIndex: QuantityIndex) =>
  setup.simulationInputs.map(input => ({
    modelName: input.name,
    quantityName: quantityIndex[input.quantityId]?.name ?? "",
    unit: quantityIndex[input.quantityId]?.unit ?? "",
    orderExternalGap: input.orderExternalGap,
    referenceValue: input.referenceValue,
  }));

export const QuantityInputInfo = ({
  setup,
  quantityIndex,
  openModal,
  projectId,
}: {
  openModal: (name: string) => void;
  setup: Setup;
  quantityIndex: QuantityIndex;
  projectId: string;
}) => {
  const canUserEditSetup = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const [search, setSearch] = useInputBindingSearch({ scope: setup.id });
  const [sort, setSort] = useSortInput({ scope: setup.id });
  const simulationInputs = React.useMemo(
    () => computeInputs(setup, quantityIndex),
    [quantityIndex, setup],
  );
  const inputMetadata = React.useMemo<{ [key in Column]: ColumnMetaData<ModelBinding> }>(
    () => ({
      name: {
        label: t("setup.simulation.table.quantities"),
        render: (b: ModelBinding) => b.quantityName,
        minWidth: "200px",
        sortable: true,
      },
      modelName: {
        label: t("setup.simulation.table.input"),
        render: (b: ModelBinding) => b.modelName,
        minWidth: "200px",
        sortable: true,
      },
      unit: {
        label: (
          <div className={labelHintContent}>
            {t("setup.simulation.table.unit")}
            <TooltipV2
              overlay={t("setup.diagnosis.observable.table.column.unit.hint")}
              className={labelHint}
            >
              <div>
                <Link size="s" />
              </div>
            </TooltipV2>
          </div>
        ),
        render: (b: ModelBinding) => b.unit,
        minWidth: "100px",
        sortable: true,
      },
      orderExternalGap: {
        label: t("setup.simulation.table.orderExternalGap"),
        render: (b: ModelBinding) => b.orderExternalGap,
        minWidth: "100px",
        sortable: true,
      },
      referenceValue: {
        label: t("setup.simulation.table.referenceValue"),
        render: (b: ModelBinding) => b.referenceValue,
        minWidth: "100px",
        sortable: true,
      },
    }),
    [],
  );

  const [filteredData] = useFilteredDatas(simulationInputs, search, input => input.quantityName);

  const sortedInputBinding = useSortedData(filteredData, sortSelector[sort.column], sort.direction);

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );
  return (
    <TableCard
      title=""
      headerLeft={
        simulationInputs.length > 0 ? (
          <SearchInputV2
            data-cy="search-project"
            placeholder={t("commons.placeholder.search")}
            onChange={handleSearchChange}
            value={search}
          />
        ) : null
      }
      headerRight={
        simulationInputs.length > 0 &&
        canUserEditSetup && (
          <Menu
            arrowWidth={0}
            // gap={16}
            placement="bottom-end"
            overlay={
              <>
                <OptionGroup>
                  <ExportArtifactOption
                    projectId={projectId}
                    setupId={setup.id}
                    section="model_binding"
                  />
                  <Option closeOnClick onClick={() => openModal("replaceInput")}>
                    <>
                      <UploadLine color={color.carbon800} />
                      {t("commons.message_card.replace")}
                    </>
                  </Option>
                  <Option closeOnClick onClick={() => openModal("deleteInput")}>
                    <DeleteBinLine color={color.carbon800} />
                    {t("commons.message_card.delete")}
                  </Option>
                </OptionGroup>
              </>
            }
          >
            <ButtonIconV2>
              <More2Fill size="m" />
            </ButtonIconV2>
          </Menu>
        )
      }
      className={cx(TableCardWithoutBorder, TableHeaderNoPaddingT)}
    >
      {simulationInputs.length > 0 ? (
        <Table
          stickyHeader
          datas={sortedInputBinding}
          metadata={inputMetadata}
          sort={sort}
          onSort={setSort}
          computeRowKey={computeRowKey}
        />
      ) : (
        <Card>
          <StyledBlankStateV2
            title={t("setup.paragraphs.quantities.input.blank_title")}
            subtitle={
              canUserEditSetup ? t("setup.paragraphs.quantities.input.blank_subtitle") : null
            }
            onClick={canUserEditSetup ? () => openModal("importInput") : undefined}
            buttonLabel={
              canUserEditSetup ? t("setup.paragraphs.quantities.input.import.button") : undefined
            }
          />
        </Card>
      )}
    </TableCard>
  );
};

interface ImportInputBindingProps {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
}

export const ImportInputBinding = ({ project, setup, onRequestClose }: ImportInputBindingProps) => {
  const { addToast } = useToasts();
  const updateSimulationInputs = usePost(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/simulation/inputs",
  );

  const projectId = project.id;
  const setupId = setup.id;
  const quantities = useQuantities(projectId);

  const [filename, setFilename] = React.useState("");
  const [isValid, setIsValid] = React.useState<boolean>();
  const [errorText, setErrorText] = React.useState<string>();
  const [errorDetails, setErrorDetails] = React.useState<InputModelParsingError[]>();
  const [inputModelParsingResult, setInputModelParsingResult] =
    React.useState<InputModelParsingResult>();

  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalyses = useImpactedAnalyses({
    setups: useSetups(projectId),
    activeAnalyses,
    setupId,
    analysisStatus,
  });

  const corpus = useMemo(
    () =>
      quantities.reduce<Record<string, string | null>>(
        (acc, { name, formula }) => ((acc[name] = formula), acc),
        {},
      ),
    [quantities],
  );
  const graph = useMemo(() => {
    let valid, graph;
    try {
      ({ graph, valid } = getCorpusGraph(corpus));
    } catch (e) {
      return null;
    }

    return valid ? (graph as { [token: string]: string[] | null }) : null;
  }, [corpus]);
  const expressionEngineInput: {
    corpus: Record<string, string | null>;
    graph: {
      [token: string]: string[] | null;
    } | null;
  } = useMemo(() => ({ corpus, graph }), [corpus, graph]);

  const isInputValid = isValid && (inputModelParsingResult?.data?.length || 0) > 0;
  const showAdditionalMessage =
    isInputValid && setup.simulationStatus === "VALID" && impactedAnalyses.length > 0;

  const handleDrop = React.useCallback(
    async ([file]: File[]) => {
      if (!file) return;

      /**
       * Keep track of the file name to display inside the dropzone.
       */
      setFilename(file.name);

      /**
       * Parse file according to the expected format and content rules.
       */
      const result = await parseInputModelFile(file, {
        expectedQuantityNameValues: quantities.map(({ name }) => name),
        quantityIdByName: quantities.reduce<Record<string, string>>(
          (acc, q) => ((acc[q.name] = q.id), acc),
          {},
        ),
        expressionEngineInput,
      });

      /**
       * Catch for parsing errors.
       * Display humain readable error message and allow for downloading the error details.
       */
      if (isResultErr(result) && result.value[0]) {
        setIsValid(false);
        setErrorText(getInputModelParsingErrorMessage(result.value[0]));
        setErrorDetails(result.value);
        return;
      }

      /**
       * Parsing was successful.
       * Save the result in the state to be used in the submit handler.
       */
      setIsValid(true);
      setInputModelParsingResult(result.value as InputModelParsingResult);
    },
    [quantities, expressionEngineInput],
  );

  const downloadErrorDetails = React.useCallback(() => {
    const errors = JSON.stringify(errorDetails, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([errors], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_input_model_import_errors.json`);
    a.click();
  }, [errorDetails]);

  const handleSubmit = React.useCallback(async () => {
    if (inputModelParsingResult) {
      await updateSimulationInputs({
        routeParams: { projectId, setupId },
        body: { fileName: filename, inputs: inputModelParsingResult.data },
      });
    }

    addToast("success", t("setup.simulation.import_batch.checking"));
    onRequestClose();
    return true;
  }, [
    addToast,
    inputModelParsingResult,
    updateSimulationInputs,
    projectId,
    setupId,
    filename,
    onRequestClose,
  ]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={isInputValid ? handleSubmit : false}
      okButtonName={t("modalForm.button.name.import")}
      cancellable
    >
      <DropzoneV2
        onDrop={handleDrop}
        accept=".csv"
        nameFile={filename}
        validation={isValid}
        pathSampleFile="/metroscopia_example_binding.csv"
        multiple={false}
        errorText={isValid ? undefined : errorText}
        downloadErrors={downloadErrorDetails}
      />
      {showAdditionalMessage ? (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.modal.delete.analysis.additionnal_message.warning", {
            simulationValid: setup.simulationStatus === "VALID",
            diagnosisValid: setup.diagnosisStatus === "COMPLETE",
            impactedAnalysisCount: impactedAnalyses.length,
          })}
        />
      ) : null}
    </ModalForm>
  );
};

//#region CSS
const StyledBlankStateV2 = styled(BlankStateV2)`
  padding: 25px 20px;
`;

const labelHint = css`
  max-width: 200px;
`;

const labelHintContent = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  svg {
    display: block;
  }
`;
//#endregion CSS
